/**
 * Test Bed API — manual types and fetchers for prompt testing endpoints.
 * These are not generated by Orval because they are new endpoints not yet
 * in the OpenAPI schema. Run `bun run codegen` after schema regeneration
 * to replace this file with generated equivalents.
 */
import { useMemo } from "react";

import { BASECAMP_API_BASE as BASE, apiFetch } from "./api-fetch";
import { useListAssessmentFileKinds } from "./types-and-hooks";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TestFile {
  file_uuid: string;
  original_filename: string;
  file_kind: string;
  mime_type: string;
  s3_key: string;
  uploaded_at: string;
  parse_status: "pending" | "parsing" | "complete" | "failed";
  workflow_id?: string;
}

export interface TestFilePresignOutput {
  file_uuid: string;
  upload_url: string;
  s3_key: string;
}

export interface ParseStatusOutput {
  status: "parsing" | "complete" | "failed";
  parsed_json: string | null;
}

export interface PromptTestInput {
  prompt_key: string;
  test_files: { file_kind: string; parsed_json: string; filename: string }[];
  mock_learner_data?: MockLearnerData;
  prompt_override?: string;
}

export interface BiographicInfo {
  name?: string;
  age?: string;
  gender?: string;
}

export interface MockLearnerData {
  biographic_info?: BiographicInfo;
  date_of_report?: string;
  safety_concerns?: string[];
  short_term_goals?: string[];
  provider_language?: string | null;
  custom_fields?: Record<string, string>;
}

export interface PromptTestStatusOutput {
  status: "running" | "complete" | "failed";
  result: string | null;
}

export interface AssessmentTool {
  value: string;
  label: string;
}

export async function listAssessmentTools(report_type: string): Promise<AssessmentTool[]> {
  const params = new URLSearchParams({ report_type });
  return apiFetch<AssessmentTool[]>(`${BASE}/assessment-tools?${params}`);
}

/** Look up the friendly `label` for a file_kind enum value (falls back to
 *  the raw value if the kind has no label entry — e.g. an org's custom
 *  tool). Both the template sandbox and the profile editor's file list
 *  render this to avoid showing raw enums like `CLINICAL_NOTES`. */
export function getFileKindLabel(value: string): string {
  return FILE_KIND_LABELS[value] ?? value;
}

// Display labels for known file kinds. NOT the source of dropdown
// values — those come from GET /inputs/file-kinds (the FDM enum) via
// useFileKindOptions, so newly registered kinds (e.g. BDI-3) show up
// without a basecamp change. Unknown kinds fall back to the raw value.
const FILE_KIND_LABELS: Record<string, string> = {
  CLINICAL_NOTES: "Clinical Notes",
  DIAGNOSIS_REPORT: "Diagnosis Report",
  BEHAVIORAL_OBSERVATIONS: "Behavioral Observations",
  BHTIA: "BHTIA",
  PREVIOUS_ASSESSMENT: "Prior Report",
  ADI_R_INTERVIEW_NOTES: "ADI-R Interview Notes",
  INTERVIEW: "Interview",
  INTERVIEW_PARENT: "Interview (Parent)",
  INTERVIEW_CHILD: "Interview (Child)",
  INTERVIEW_TEACHER: "Interview (Teacher)",
  INTERVIEW_OTHER: "Interview (Other)",
  INTAKE_CALL: "Intake Call",
  ABAS: "ABAS",
  ABLLS: "ABLLS",
  ACTION_BEHAVIORAL_CENTER_INTAKE: "Action Behavioral Center Intake",
  ADI_R: "ADI-R",
  ADI_R_TODDLER: "ADI-R Toddler",
  ADOS: "ADOS",
  ADOS_2: "ADOS-2",
  AFLS: "AFLS",
  APSI: "APSI",
  ASRS: "ASRS",
  BASC_3: "BASC-3",
  BASC_3_PRQ: "BASC-3 PRQ",
  BAYLEY_3: "Bayley-3",
  BAYLEY_4: "Bayley-4",
  BDI_2: "BDI-2",
  BEFAS: "BEFAS",
  BRIEF: "BRIEF",
  BRIEF_2: "BRIEF-2",
  CARS_2: "CARS-2",
  CATALIGHT_INTAKE: "Catalight Intake",
  CBCL: "CBCL",
  CFQL: "CFQL",
  CONNERS_4_ADHD: "Conners-4 ADHD",
  CONNERS_CPT3_ADHD: "Conners CPT-3 ADHD",
  CTONI_2: "CTONI-2",
  DAS_2: "DAS-2",
  DAYC: "DAYC",
  DP_4: "DP-4",
  EARLIPOINT: "Earlipoint",
  ESDM: "ESDM",
  ESSENTIAL_FOR_LIVING: "Essential for Living",
  ESSENTIAL_FOR_LIVING_QUICK: "Essential for Living (Quick)",
  EVT_3: "EVT-3",
  FAST: "FAST",
  GARS_2: "GARS-2",
  GARS_3: "GARS-3",
  HANLEY_FBA: "Hanley FBA",
  KBIT_2: "KBIT-2",
  MAS: "MAS",
  MIGDAS_2: "MIGDAS-2",
  MULLEN_SCALES_OF_EARLY_LEARNERS: "Mullen Scales of Early Learning",
  M_CHAT: "M-CHAT",
  NICHQ: "NICHQ",
  PDBBI_PARENT: "PDBBI (Parent)",
  PDBBI_TEACHER: "PDBBI (Teacher)",
  PDDBI: "PDDBI",
  PDPVTS: "PDPVTS",
  PEAK: "PEAK",
  PLS_5: "PLS-5",
  PPVT_5: "PPVT-5",
  PSI_4: "PSI-4",
  P_TONI: "P-TONI",
  QABF: "QABF",
  RCMAS_2: "RCMAS-2",
  RIAS_2: "RIAS-2",
  SCARED: "SCARED",
  SCQ: "SCQ",
  SENSORY_PROFILE_2: "Sensory Profile-2",
  SIPA: "SIPA",
  SKILL_STREAMING: "Skill Streaming",
  SOCIALLY_SAVVY: "Socially Savvy",
  SOCIAL_SKILLS_SOLUTIONS: "Social Skills Solutions",
  SPM_2: "SPM-2",
  SRS2: "SRS-2",
  SSIS: "SSIS",
  STANFORD_BINET: "Stanford-Binet",
  STANFORD_BINET_5: "Stanford-Binet 5",
  TASI: "TASI",
  TELE_ASD_PEDS: "Tele-ASD-Peds",
  TONI_4: "TONI-4",
  TSCC: "TSCC",
  TSCYC: "TSCYC",
  VBMAPP: "VB-MAPP",
  VBMAPP_LEARN: "VB-MAPP (LEARN)",
  VBMAPP_OPYA: "VB-MAPP (OPYA)",
  VINELAND: "Vineland",
  WAIS_4: "WAIS-4",
  WAIS_V: "WAIS-V",
  WASI_II: "WASI-II",
  WIAT_IV: "WIAT-IV",
  WISC_V: "WISC-V",
  WPPSI_IV: "WPPSI-IV",
  WURS: "WURS",
  BDI_3: "BDI-3",
};

// Curated dropdown ordering: common clinical/interview kinds first,
// everything else (assessments) alphabetically.
const PRIORITY_FILE_KINDS = [
  "CLINICAL_NOTES",
  "DIAGNOSIS_REPORT",
  "BEHAVIORAL_OBSERVATIONS",
  "BHTIA",
  "PREVIOUS_ASSESSMENT",
  "ADI_R_INTERVIEW_NOTES",
  "INTERVIEW",
  "INTERVIEW_PARENT",
  "INTERVIEW_CHILD",
  "INTERVIEW_TEACHER",
  "INTERVIEW_OTHER",
  "INTAKE_CALL",
];

export interface FileKindOption {
  value: string;
  label: string;
}

// Kinds the ephemeral parser can't process — uploading one completes the
// parse workflow with no output, so the file just lands "failed".
// INTAKE_CALL is the intake kind that parses; the TRANSCRIPT variants are
// pipeline-internal artifacts, and UNCLASSIFIED has no parser at all.
const UNPARSEABLE_UPLOAD_KINDS = new Set(["INTAKE_CALL_TRANSCRIPT", "INTAKE_CALL_TRANSCRIPT_JSON", "UNCLASSIFIED"]);

/**
 * File-kind options for upload dropdowns, sourced from the live FDM
 * enum (GET /inputs/file-kinds) — the same query the New Input dialog
 * uses — so the list can never go stale against the platform.
 */
export function useFileKindOptions(): { options: FileKindOption[]; isLoading: boolean } {
  const { data, isLoading } = useListAssessmentFileKinds();
  const options = useMemo<FileKindOption[]>(() => {
    if (data?.status !== 200) return [];
    const values = (data.data.items ?? []).map((i) => i.key).filter((k) => !UNPARSEABLE_UPLOAD_KINDS.has(k));
    const prioritized = PRIORITY_FILE_KINDS.filter((v) => values.includes(v));
    const rest = values.filter((v) => !PRIORITY_FILE_KINDS.includes(v)).sort();
    return [...prioritized, ...rest].map((value) => ({ value, label: getFileKindLabel(value) }));
  }, [data]);
  return { options, isLoading };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function presignTestFile(params: {
  template_id?: number | null;
  org_id?: string | null;
  shared?: boolean;
  file_kind: string;
  mime_type: string;
  original_filename: string;
}): Promise<TestFilePresignOutput> {
  return apiFetch<TestFilePresignOutput>(`${BASE}/test-files/presign`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function listTestFiles(template_id: number, org_id?: string | null): Promise<TestFile[]> {
  const params = new URLSearchParams({ template_id: String(template_id) });
  if (org_id) params.set("org_id", org_id);
  return apiFetch<TestFile[]>(`${BASE}/test-files/list?${params}`);
}

export async function listSharedTestFiles(org_id: string | null): Promise<TestFile[]> {
  // A null / empty org_id targets the truly-global pool
  // (`test-files/_global_/global/…`) shared across every org — used by
  // Evaluations profiles with no org owner.
  const params = new URLSearchParams();
  if (org_id) params.set("org_id", org_id);
  const suffix = params.toString() ? `?${params}` : "";
  return apiFetch<TestFile[]>(`${BASE}/test-files/shared${suffix}`);
}

export async function deleteTestFile(params: {
  template_id?: number | null;
  org_id?: string | null;
  shared?: boolean;
  file_uuid: string;
}): Promise<void> {
  await apiFetch<void>(`${BASE}/test-files/delete`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function startTestFileParse(params: {
  template_id?: number | null;
  org_id?: string | null;
  shared?: boolean;
  file_uuid: string;
  s3_key: string;
  file_kind: string;
  original_filename: string;
  mime_type?: string;
}): Promise<{ workflow_id: string }> {
  return apiFetch<{ workflow_id: string }>(`${BASE}/test-files/parse`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getTestFileParseStatus(
  workflow_id: string,
  opts: { template_id?: number | null; org_id?: string | null; shared?: boolean; file_uuid: string },
): Promise<ParseStatusOutput> {
  const params = new URLSearchParams({ file_uuid: opts.file_uuid });
  if (opts.template_id != null) params.set("template_id", String(opts.template_id));
  if (opts.org_id) params.set("org_id", opts.org_id);
  if (opts.shared) params.set("shared", "true");
  return apiFetch<ParseStatusOutput>(`${BASE}/test-files/parse-status/${workflow_id}?${params}`);
}

export async function startPromptTest(input: PromptTestInput): Promise<{ workflow_id: string }> {
  return apiFetch<{ workflow_id: string }>(`${BASE}/prompts/test`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getPromptTestStatus(workflow_id: string): Promise<PromptTestStatusOutput> {
  return apiFetch<PromptTestStatusOutput>(`${BASE}/prompts/test-status/${workflow_id}`);
}
